import { createContext } from 'react';
import { ROZOSTRENI_NAVIGACE } from '../../lib/uiTokens';

/**
 * Hlavička seznamu ve skle (`ListToolbar sklo`) to přes tenhle kontext řekne
 * ovladačům, které do ní moduly vkládají hotové (`FiltrTlacitko`, „…"
 * v `NabidkaAkci`) — ty se pak nakreslí jako skleněný kruh samy, bez dalšího
 * přepínače v každém modulu.
 */
export const SkloCtx = createContext(false);

/**
 * Skleněné ovládání u horní hrany na mobilu — kruhy a pilulky.
 *
 * Stejný jazyk, jaký od iOS 26 mají systémové aplikace (WhatsApp, Zprávy):
 * horní pruh s pozadím a linkou zmizel a každé ovládání je samostatná
 * „kapka" mléčného skla. Materiál je **tentýž jako spodní lišta**
 * (`MobilniLista`, `ROZOSTRENI_NAVIGACE`) — horní a dolní ovládání jsou dvě
 * plochy jednoho systému a nemají vypadat každá z jiného skla.
 *
 * Pravidla (standard-rozvrzeni.md, „Horní ovládání na mobilu"):
 *  - jedno ovládání = **kruh** 44 px (`SkloKruh`)
 *  - dvě a víc souvisejících akcí = **jedna pilulka** (`SkloPilulka`)
 *  - hlavní akce obrazovky (přidat, napsat) = **plný kruh v barvě sekce**,
 *    na obrazovce nejvýš jeden; ostatní jsou neutrální sklo
 *  - vlevo návrat nebo nic, vpravo akce
 */
export function skloMaterial(isDark) {
    return {
        backdropFilter: ROZOSTRENI_NAVIGACE,
        WebkitBackdropFilter: ROZOSTRENI_NAVIGACE,
        ...(isDark ? {
            background: 'rgba(255,255,255,0.08)',
            boxShadow: '0 6px 20px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.22), inset 0 -6px 14px rgba(255,255,255,0.04)',
        } : {
            background: 'rgba(255,255,255,0.62)',
            boxShadow: '0 6px 18px rgba(30,30,50,0.14), inset 0 1px 0 rgba(255,255,255,0.95), inset 0 -6px 14px rgba(255,255,255,0.3)',
        }),
    };
}

const zaklad = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, border: 'none', padding: 0, cursor: 'pointer',
    WebkitTapHighlightColor: 'transparent', textDecoration: 'none',
};

/**
 * Jedno ovládání — skleněný kruh. S `hlavni` plný kruh v `barva`.
 * S `href` se kreslí jako odkaz (tel:, mailto:).
 */
export function SkloKruh({ Icon, popis, onClick, href, isDark, hlavni = false, barva, velikost = 44, ikona = 20, disabled, style }) {
    const vzhled = hlavni
        ? { background: barva, color: '#fff', boxShadow: `0 6px 18px ${isDark ? 'rgba(0,0,0,0.4)' : 'rgba(30,30,50,0.2)'}` }
        : { ...skloMaterial(isDark), color: 'var(--text-primary)' };
    const props = {
        'aria-label': popis, title: popis,
        style: { ...zaklad, width: velikost, height: velikost, borderRadius: 999, ...vzhled, opacity: disabled ? 0.5 : 1, ...style },
    };
    const obsah = <Icon size={ikona} strokeWidth={hlavni ? 2.4 : 2} />;
    return href
        ? <a href={href} {...props}>{obsah}</a>
        : <button type="button" onClick={onClick} disabled={disabled} {...props}>{obsah}</button>;
}

/**
 * Skupina souvisejících akcí v jedné pilulce (volat + info, jako video +
 * telefon ve WhatsAppu). Položky: `{ id, Icon, popis, onClick?, href? }`.
 * Jediná položka se kreslí jako `SkloKruh` — pilulka o jednom prvku je kruh.
 */
export function SkloPilulka({ polozky = [], isDark, velikost = 44, ikona = 20, style }) {
    const viditelne = polozky.filter(Boolean);
    if (!viditelne.length) return null;
    if (viditelne.length === 1) {
        const p = viditelne[0];
        return <SkloKruh {...p} isDark={isDark} velikost={velikost} ikona={ikona} style={style} />;
    }
    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', height: velikost, borderRadius: 999, padding: '0 4px', flexShrink: 0, ...skloMaterial(isDark), ...style }}>
            {viditelne.map(p => {
                const props = {
                    'aria-label': p.popis, title: p.popis,
                    style: { ...zaklad, width: velikost - 2, height: velikost, background: 'transparent', color: 'var(--text-primary)' },
                };
                return p.href
                    ? <a key={p.id} href={p.href} {...props}><p.Icon size={ikona} /></a>
                    : <button key={p.id} type="button" onClick={p.onClick} {...props}><p.Icon size={ikona} /></button>;
            })}
        </div>
    );
}
